/**
 * Firebase Service - Modular API
 *
 * This module provides Firebase functionality using the modular SDK for better tree-shaking.
 * Only the specific functions used are imported, significantly reducing bundle size.
 */

import { initializeApp, getApps, getApp } from "firebase/app";
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInAnonymously as firebaseSignInAnonymously,
  GoogleAuthProvider,
} from "firebase/auth";
import {
  getDatabase,
  ref as dbRef,
  get,
  set,
  update,
  remove,
  push,
  query,
  orderByChild,
  equalTo,
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import axios from "axios";
import { debugTimeStart, debugTimeEnd } from "utils/utils";
import { updateGeoJsonBoundary } from "api/adminUtils";
import { captureException } from "api/sentry";

// Firebase app instance - initialized once
let app = null;
let db = null;
let storage = null;
let auth = null;

// Cache for customization objects
let customizationObjects = [];

/**
 * Gets the Firebase app instance, initializing if needed
 * @returns {FirebaseApp}
 */
export function getFirebaseApp() {
  if (!app) {
    throw new Error("Firebase not initialized. Call init() first.");
  }
  return app;
}

/**
 * Creates the Auth instance like getAuth() does, minus the popup/redirect
 * resolver: getAuth registers browserPopupRedirectResolver, which makes Auth
 * load Google's gapi (api.js and an auth iframe) at startup. Only the Google
 * sign-in popup needs it, so those calls pass it themselves. Same persistence
 * as getAuth, so signed-in users stay signed in. Must be the first Auth call
 * on the app: a getAuth(app) after it returns this same instance.
 */
function createAuth(firebaseApp) {
  const instance = initializeAuth(firebaseApp, {
    persistence: [
      indexedDBLocalPersistence,
      browserLocalPersistence,
      browserSessionPersistence,
    ],
  });
  // getAuth() also honours an auth emulator host from __FIREBASE_DEFAULTS__.
  const emulatorHost = globalThis.__FIREBASE_DEFAULTS__?.emulatorHosts?.auth;
  if (emulatorHost) {
    connectAuthEmulator(instance, `http://${emulatorHost}`);
  }
  return instance;
}

/**
 * Gets the Firebase Auth instance
 * @returns {Auth}
 */
export function getFirebaseAuth() {
  if (!auth) {
    auth = createAuth(getFirebaseApp());
  }
  return auth;
}

/**
 * Gets the Firebase Database instance
 * @returns {Database}
 */
export function getFirebaseDatabase() {
  if (!db) {
    db = getDatabase(app);
  }
  return db;
}

/**
 * Gets the Firebase Storage instance
 * @returns {FirebaseStorage}
 */
export function getFirebaseStorage() {
  if (!storage) {
    storage = getStorage(app);
  }
  return storage;
}

/**
 * Initializes Firebase with given config.
 */
function init() {
  debugTimeStart("init");
  try {
    const firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG);
    if (process.env.REACT_APP_USE_RTD_EMULATOR === "true") {
      firebaseConfig.databaseURL =
        "http://localhost:9000?ns=prod-simulator-earthmap";
    }

    // Initialize app only if not already initialized
    if (getApps().length === 0) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApp();
    }

    // Initialize services
    auth = createAuth(app);
    db = getDatabase(app);
    storage = getStorage(app);

    // Initialize Firebase App Check for bot protection
    try {
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

      if (!siteKey) {
        console.warn(
          "Firebase App Check: No reCAPTCHA site key configured. Bot protection is disabled.",
        );
      } else {
        // Enable debug mode for localhost
        if (
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1"
        ) {
          self.FIREBASE_APPCHECK_DEBUG_TOKEN =
            "463B8149-8A75-4A7B-95D8-8A4312A796DF";
          console.log("Firebase App Check: Debug mode enabled for localhost");
        }

        initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(siteKey),
          isTokenAutoRefreshEnabled: true,
        });

        console.log(
          "Firebase App Check: Initialized successfully with reCAPTCHA Enterprise",
        );
      }
    } catch (appCheckError) {
      console.error("Firebase App Check: Initialization failed", appCheckError);
    }
  } finally {
    debugTimeEnd("init");
  }
}

/**
 * Signs the user out from Firebase.
 * @returns {Promise<void>}
 */
function signOut() {
  debugTimeStart("signOut");
  return firebaseSignOut(getFirebaseAuth()).finally(() => {
    debugTimeEnd("signOut");
  });
}

// ============================================================================
// AUTH METHODS FOR CUSTOM AUTH UI
// ============================================================================

/**
 * Signs in with Google using popup.
 * @returns {Promise<UserCredential>}
 */
export async function authSignInWithGoogle() {
  debugTimeStart("authSignInWithGoogle");
  try {
    const auth = getFirebaseAuth();
    const provider = new GoogleAuthProvider();
    return await signInWithPopup(auth, provider, browserPopupRedirectResolver);
  } finally {
    debugTimeEnd("authSignInWithGoogle");
  }
}

/**
 * Signs in with email and password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
export async function authSignInWithEmail(email, password) {
  debugTimeStart("authSignInWithEmail");
  try {
    return await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  } finally {
    debugTimeEnd("authSignInWithEmail");
  }
}

/**
 * Creates a new account with email and password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
export async function authCreateAccount(email, password) {
  debugTimeStart("authCreateAccount");
  try {
    return await createUserWithEmailAndPassword(
      getFirebaseAuth(),
      email,
      password,
    );
  } finally {
    debugTimeEnd("authCreateAccount");
  }
}

/**
 * Sends a password reset email.
 * @param {string} email
 * @returns {Promise<void>}
 */
export async function authResetPassword(email) {
  debugTimeStart("authResetPassword");
  try {
    return await sendPasswordResetEmail(getFirebaseAuth(), email);
  } finally {
    debugTimeEnd("authResetPassword");
  }
}

/**
 * Signs in anonymously.
 * @returns {Promise<UserCredential>}
 */
export async function authSignInAnonymously() {
  debugTimeStart("authSignInAnonymously");
  try {
    return await firebaseSignInAnonymously(getFirebaseAuth());
  } finally {
    debugTimeEnd("authSignInAnonymously");
  }
}

/**
 * Gets the available languages from the 'locale' node in Realtime Database.
 * @returns {Promise<any>}
 */
function getAvailableLanguages() {
  debugTimeStart("getAvailableLanguages");
  return get(dbRef(getFirebaseDatabase(), "locale"))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getAvailableLanguages");
    });
}

/**
 * Gets the user's token to use in API requests (Refresh token).
 * @returns {Promise<string | null>}
 */
function getUserAPIBearer() {
  debugTimeStart("getUserAPIBearer");
  return new Promise((resolve, reject) => {
    const user = getFirebaseAuth().currentUser;
    if (user && user.isAnonymous === false) {
      resolve(user.refreshToken);
    } else if (user && user.isAnonymous) {
      resolve(null);
    } else {
      reject("No user is currently authenticated");
    }
  }).finally(() => {
    debugTimeEnd("getUserAPIBearer");
  });
}

/**
 * Fetches translations for a specific locale.
 * @param {string} locale
 * @returns {Promise<any>}
 */
function getTranslations(locale) {
  debugTimeStart("getTranslations");
  return get(dbRef(getFirebaseDatabase(), `translations/${locale}`))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getTranslations");
    });
}

/**
 * Fetches a list of all areas from Realtime Database.
 * @returns {Promise<any>}
 */
function getAreasList() {
  debugTimeStart("getAreasList");
  return get(dbRef(getFirebaseDatabase(), "areasList"))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getAreasList");
    });
}

/**
 * Fetches data for a specific area by ID.
 * @param {string} areaId
 * @returns {Promise<any>}
 */
function getArea(areaId) {
  debugTimeStart("getArea");
  return get(dbRef(getFirebaseDatabase(), `areas/${areaId}`))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getArea");
    });
}

/**
 * Fetches all areas from Realtime Database.
 * @returns {Promise<any>}
 */
function getAreas() {
  debugTimeStart("getAreas");
  return get(dbRef(getFirebaseDatabase(), "areas"))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getAreas");
    });
}

/**
 * Gets customization data from the local cache if available;
 * otherwise, fetches from Realtime Database.
 * @param {string} customizationName
 * @param {boolean} forceRefresh
 * @returns {Promise<any>}
 */
function getCustomization(customizationName, forceRefresh = false) {
  debugTimeStart("getCustomization");
  if (!forceRefresh && customizationObjects[customizationName]) {
    debugTimeEnd("getCustomization");
    return Promise.resolve(customizationObjects[customizationName]);
  } else {
    return get(
      dbRef(getFirebaseDatabase(), `customizations/${customizationName}`),
    )
      .then((snapshot) => {
        customizationObjects[customizationName] = snapshot.val();
        return customizationObjects[customizationName];
      })
      .finally(() => {
        debugTimeEnd("getCustomization");
      });
  }
}

/**
 * Fetches boundaries for a given areaId.
 * @param {string} areaId
 * @returns {Promise<any>}
 */
function getBoundaries(areaId) {
  debugTimeStart("getBoundaries");
  return get(dbRef(getFirebaseDatabase(), `boundaries/${areaId}`))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getBoundaries");
    });
}

/**
 * Fetches data for a specific layer by ID.
 * @param {string} layerId
 * @returns {Promise<any>}
 */
function getLayer(layerId) {
  debugTimeStart("getLayer");
  return get(dbRef(getFirebaseDatabase(), `layers/${layerId}`))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getLayer");
    });
}

/**
 * Fetches all layers from Realtime Database.
 * @returns {Promise<any>}
 */
function getLayers() {
  debugTimeStart("getLayers");
  return get(dbRef(getFirebaseDatabase(), "layers"))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getLayers");
    });
}

/**
 * Fetches layer groups from Realtime Database.
 * @returns {Promise<any>}
 */
function getLayersGroups() {
  debugTimeStart("getLayersGroups");
  return get(dbRef(getFirebaseDatabase(), "layersGroups"))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getLayersGroups");
    });
}

/**
 * Fetches all scripts from Realtime Database.
 * @returns {Promise<any>}
 */
function getScripts() {
  debugTimeStart("getScripts");
  return get(dbRef(getFirebaseDatabase(), "scripts"))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getScripts");
    });
}

/**
 * Fetches metadata for scripts from Realtime Database.
 * @returns {Promise<any>}
 */
function getScriptsMetadata() {
  debugTimeStart("getScriptsMetadata");
  return get(dbRef(getFirebaseDatabase(), "scripts"))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getScriptsMetadata");
    });
}

/**
 * Fetches data for a single script by ID.
 * @param {string} scriptId
 * @returns {Promise<any>}
 */
function getScript(scriptId) {
  debugTimeStart("getScript");
  return get(dbRef(getFirebaseDatabase(), `scripts/${scriptId}`))
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("getScript");
    });
}

/**
 * Gets a download URL from Firebase Storage for a given path.
 * @param {string} path
 * @returns {Promise<string>}
 */
function getDownloadUrl(path) {
  debugTimeStart("getDownloadUrl");
  return getDownloadURL(storageRef(getFirebaseStorage(), path)).finally(() => {
    debugTimeEnd("getDownloadUrl");
  });
}

/**
 * Adds a user boundary (a GeoJSON file) to Firebase Storage and Realtime Database.
 * @param {string} userId
 * @param {string} boundaryName
 * @param {Object} geoJson
 * @param {string} areaId
 * @param {string} [geoId] - FAO GeoID registry id, when the boundary was registered
 * @returns {Promise<Object>} The boundary data, including boundaryId and geojsonSrc
 */
async function addUserBoundary(
  userId,
  boundaryName,
  geoJson,
  areaId,
  geoId,
  description,
) {
  debugTimeStart("addUserBoundary");
  try {
    const database = getFirebaseDatabase();
    const boundaryPushRef = push(
      dbRef(database, `/users/${userId}/boundaries/`),
    );
    const boundaryId = boundaryPushRef.key;
    const boundaryRefPath = `users/${userId}/boundaries/${boundaryId}.geojson`;
    const uploadResponse = await updateGeoJsonBoundary(
      boundaryRefPath,
      geoJson,
    );

    if (uploadResponse?.success) {
      // Boundaries saved before the switch to the FAO GeoID registry carry an
      // `agStackId` instead; readers fall back to it, but nothing writes it now.
      const boundary = {
        name: boundaryName,
        geojsonSrc: boundaryRefPath,
        areaId,
        geoId: geoId || "",
        description: description || "",
      };

      const updates = {};
      updates[`/users/${userId}/boundaries/${boundaryId}`] = boundary;

      await update(dbRef(database), updates);
      return { boundaryId, geojsonSrc: boundaryRefPath };
    }
    throw new Error("Failed to upload boundary");
  } finally {
    debugTimeEnd("addUserBoundary");
  }
}

/**
 * Loads user boundaries for a specific area.
 * @param {string} userId
 * @param {string} areaId
 * @returns {Promise<any>}
 */
function loadUserBoundaries(userId, areaId) {
  debugTimeStart("loadUserBoundaries");
  const boundariesRef = dbRef(
    getFirebaseDatabase(),
    `users/${userId}/boundaries`,
  );
  const boundariesQuery = query(
    boundariesRef,
    orderByChild("areaId"),
    equalTo(areaId),
  );
  return get(boundariesQuery)
    .then((snapshot) => snapshot.val())
    .finally(() => {
      debugTimeEnd("loadUserBoundaries");
    });
}

/**
 * Deletes a user boundary from Firebase Storage and removes the DB record.
 * @param {string} userId
 * @param {string} boundaryId
 * @returns {Promise<void>}
 */
/**
 * Updates editable metadata fields (name, description) on a user boundary.
 * @param {string} userId
 * @param {string} boundaryId
 * @param {{name?: string, description?: string}} fields
 * @returns {Promise<void>}
 */
async function updateUserBoundary(userId, boundaryId, fields) {
  debugTimeStart("updateUserBoundary");
  try {
    const database = getFirebaseDatabase();
    const updates = {};
    if (typeof fields.name === "string") {
      updates[`/users/${userId}/boundaries/${boundaryId}/name`] = fields.name;
    }
    if (typeof fields.description === "string") {
      updates[`/users/${userId}/boundaries/${boundaryId}/description`] =
        fields.description;
    }
    if (Object.keys(updates).length > 0) {
      await update(dbRef(database), updates);
    }
  } finally {
    debugTimeEnd("updateUserBoundary");
  }
}

async function deleteUserBoundary(userId, boundaryId) {
  debugTimeStart("deleteUserBoundary");
  try {
    const fileRef = storageRef(
      getFirebaseStorage(),
      `users/${userId}/boundaries/${boundaryId}.geojson`,
    );
    await deleteObject(fileRef);
    await remove(
      dbRef(getFirebaseDatabase(), `/users/${userId}/boundaries/${boundaryId}`),
    );
  } finally {
    debugTimeEnd("deleteUserBoundary");
  }
}

/**
 * Sets user locale in the Realtime Database.
 * @param {string} userId
 * @param {string} locale
 */
function setUserLocale(userId, locale) {
  debugTimeStart("setUserLocale");
  try {
    set(dbRef(getFirebaseDatabase(), `users/${userId}`), { locale }).catch(
      (error) => {
        throw error;
      },
    );
  } finally {
    debugTimeEnd("setUserLocale");
  }
}

/**
 * Updates user options in the Realtime Database.
 * @param {string} userId
 * @param {Object} options
 * @returns {Promise<void>}
 */
function setUserOptions(userId, options) {
  debugTimeStart("setUserOptions");
  return set(
    dbRef(getFirebaseDatabase(), `users/${userId}/config/userOptions`),
    options,
  )
    .catch((error) => {
      throw error;
    })
    .finally(() => {
      debugTimeEnd("setUserOptions");
    });
}

/**
 * Gets user options from the Realtime Database.
 * @param {string} userId
 * @returns {Promise<any>}
 */
/**
 * Context for a denied read of users/{uid}/... .
 *
 * The RTDB rule on users/$uid/config is "$uid === auth.uid", so a denial means
 * the uid we read with is not the one the SDK is authenticated as. Which
 * transition causes that on whisp is not yet known (Sentry
 * EARTHMAP-FRONTEND-47Z, -480: ~8 users, no reproduction, replay expired), and
 * the events carried nothing to tell a uid mismatch apart from a session with
 * no token at all. This records both uids so the next occurrence says which.
 *
 * @param {string} requestedUid the uid the read was made with
 */
const authReadDiagnostics = (requestedUid) => {
  let currentUser = null;
  try {
    currentUser = getFirebaseAuth().currentUser;
  } catch (e) {
    // getFirebaseAuth throws before init; that is itself worth recording.
  }
  return {
    requestedUid: requestedUid || null,
    currentUid: currentUser?.uid || null,
    hasCurrentUser: Boolean(currentUser),
    isAnonymous: currentUser?.isAnonymous ?? null,
    uidMatches: Boolean(currentUser && currentUser.uid === requestedUid),
  };
};

/** True for an RTDB rules rejection, as opposed to a network or parse failure. */
const isPermissionDenied = (error) =>
  error?.code === "PERMISSION_DENIED" ||
  /permission[_ ]denied/i.test(error?.message || "");

/**
 * Report a failed users/{uid} read once, with the diagnostics above. Callers
 * log at warn level so this stays the single Sentry event for the failure
 * (captureConsoleIntegration only captures console.error).
 */
const reportUserReadFailure = (label, requestedUid, error) => {
  captureException(error, {
    tags: {
      user_read: label,
      permission_denied: String(isPermissionDenied(error)),
    },
    extra: authReadDiagnostics(requestedUid),
  });
};

function getUserOptions(userId) {
  debugTimeStart("getUserOptions");
  return get(dbRef(getFirebaseDatabase(), `users/${userId}/config/userOptions`))
    .then((snapshot) => snapshot.val())
    .catch((error) => {
      reportUserReadFailure("userOptions", userId, error);
      throw error;
    })
    .finally(() => {
      debugTimeEnd("getUserOptions");
    });
}

/**
 * Updates the Realtime Database to not show the splash dialog again for the user.
 * @param {string} userId
 */
function doNotShowSplashDialogAgain(userId) {
  debugTimeStart("doNotShowSplashDialogAgain");
  try {
    const refPath = `users/${userId}/config/showSplashDialog/${process.env.REACT_APP_CUSTOMIZATION_NAME}`;
    const payload = {
      disabled: true,
      timestamp: Date.now(),
    };

    set(dbRef(getFirebaseDatabase(), refPath), payload).catch((error) => {
      throw error;
    });
  } finally {
    debugTimeEnd("doNotShowSplashDialogAgain");
  }
}

/**
 * Determines whether the splash dialog should be shown for the user.
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
async function shouldShowSplashDialog(userId) {
  debugTimeStart("shouldShowSplashDialog");

  if (!userId) {
    debugTimeEnd("shouldShowSplashDialog");
    return true;
  }

  try {
    const database = getFirebaseDatabase();
    const [userSnapshot, configSnapshot] = await Promise.all([
      get(
        dbRef(
          database,
          `users/${userId}/config/showSplashDialog/${process.env.REACT_APP_CUSTOMIZATION_NAME}`,
        ),
      ),
      get(
        dbRef(
          database,
          `customizations/${process.env.REACT_APP_CUSTOMIZATION_NAME}/splashDialog/lastUpdated`,
        ),
      ),
    ]);

    const userValue = userSnapshot.val();
    const configTimestamp = configSnapshot.val() || 0;

    // Accounts created after the message was last updated skip it: for a
    // brand-new user everything in the app is new, so a "what's new" splash
    // is noise on top of onboarding. They'll see the next update's splash.
    // (Only applies when the message carries a lastUpdated timestamp.)
    const creationTime = getFirebaseAuth().currentUser?.metadata?.creationTime;
    if (
      configTimestamp > 0 &&
      creationTime &&
      Date.parse(creationTime) > configTimestamp
    ) {
      return false;
    }

    if (userValue === null) {
      return true;
    }

    const disabled = userValue?.disabled;
    const userTimestamp = userValue?.timestamp || 0;

    return !disabled || userTimestamp < configTimestamp;
  } catch (error) {
    reportUserReadFailure("showSplashDialog", userId, error);
    // warn, not error: reportUserReadFailure already filed this with the
    // diagnostics, and console.error would file a second copy without them.
    console.warn("Error checking splash dialog status:", error);
    return true;
  } finally {
    debugTimeEnd("shouldShowSplashDialog");
  }
}

/**
 * Gets a GeoJSON file from Firebase Storage and retrieves its contents via Axios.
 * @param {string} boundaryId
 * @param {AbortController} abortController
 * @returns {Promise<Object>}
 */
async function getGeoJson(boundaryId, abortController) {
  debugTimeStart("getGeoJson");
  try {
    const url = await getDownloadURL(
      storageRef(getFirebaseStorage(), boundaryId),
    );
    const response = await axios.get(url, { signal: abortController.signal });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.warn("Request canceled", error.message);
      // Make the canceled-request outcome explicit so callers can bail instead
      // of dereferencing an implicit undefined.
      return null;
    } else {
      console.warn("Error downloading GeoJSON", error.message);
      throw error;
    }
  } finally {
    debugTimeEnd("getGeoJson");
  }
}

/**
 * Fetches a custom config if available in ENV or from Realtime Database.
 * @param {boolean} forceRefresh
 * @returns {Promise<any>}
 */
async function getCustomizationConfig(forceRefresh = false) {
  debugTimeStart("getCustomizationConfig");
  try {
    if (process.env.REACT_APP_CUSTOM_CONFIG) {
      return getInlineCustomizationConfig();
    } else if (process.env.REACT_APP_CUSTOMIZATION_NAME) {
      return await getCustomization(
        process.env.REACT_APP_CUSTOMIZATION_NAME,
        forceRefresh,
      );
    }
    return undefined;
  } finally {
    debugTimeEnd("getCustomizationConfig");
  }
}

// The inline REACT_APP_CUSTOM_CONFIG, parsed once. It used to be JSON.parsed on
// every call, so each of the ~25 useCustomConfig() consumers (and every store
// action reading the config) held a DIFFERENT object and no `[config]` effect
// or memo could ever be shared between them.
let inlineCustomizationConfig;
function getInlineCustomizationConfig() {
  if (inlineCustomizationConfig === undefined) {
    inlineCustomizationConfig = JSON.parse(process.env.REACT_APP_CUSTOM_CONFIG);
  }
  return inlineCustomizationConfig;
}

/**
 * The customization config if it is already in memory (inline env config, or
 * a customization fetched earlier in the session), else undefined. Synchronous
 * so a component can render with the config on its FIRST render instead of a
 * null -> object double render; getCustomizationConfig() remains the source of
 * truth and the admin's forceRefresh path updates the same cache.
 * @returns {object|undefined}
 */
function getCachedCustomizationConfig() {
  if (process.env.REACT_APP_CUSTOM_CONFIG) {
    return getInlineCustomizationConfig();
  }
  if (process.env.REACT_APP_CUSTOMIZATION_NAME) {
    return customizationObjects[process.env.REACT_APP_CUSTOMIZATION_NAME];
  }
  return undefined;
}

export default {
  addUserBoundary,
  updateUserBoundary,
  deleteUserBoundary,
  getArea,
  getAreas,
  getAreasList,
  getAvailableLanguages,
  getBoundaries,
  getCustomization,
  getCustomizationConfig,
  getCachedCustomizationConfig,
  getDownloadUrl,
  getGeoJson,
  getLayer,
  getLayers,
  getLayersGroups,
  getScripts,
  getScript,
  getScriptsMetadata,
  getTranslations,
  init,
  loadUserBoundaries,
  setUserLocale,
  signOut,
  doNotShowSplashDialogAgain,
  shouldShowSplashDialog,
  getUserOptions,
  setUserOptions,
  getUserAPIBearer,
  // Export getters for other modules
  getFirebaseApp,
  getFirebaseAuth,
  getFirebaseDatabase,
  getFirebaseStorage,
  // Auth methods for custom auth UI
  authSignInWithGoogle,
  authSignInWithEmail,
  authCreateAccount,
  authResetPassword,
  authSignInAnonymously,
};
